<script>
import { mapGetters } from 'vuex';
import { shouldBeUrl } from 'shared/helpers/Validators';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import Avatar from 'next/avatar/Avatar.vue';
import SettingIntroBanner from 'dashboard/components/widgets/SettingIntroBanner.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import inboxMixin from 'shared/mixins/inboxMixin';
import FacebookReauthorize from './facebook/Reauthorize.vue';
import InstagramReauthorize from './channels/instagram/Reauthorize.vue';
import TiktokReauthorize from './channels/tiktok/Reauthorize.vue';
import DuplicateInboxBanner from './channels/instagram/DuplicateInboxBanner.vue';
import MicrosoftReauthorize from './channels/microsoft/Reauthorize.vue';
import GoogleReauthorize from './channels/google/Reauthorize.vue';
import WhatsappReauthorize from './channels/whatsapp/Reauthorize.vue';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import PreChatFormSettings from './PreChatForm/Settings.vue';
import WeeklyAvailability from './components/WeeklyAvailability.vue';
import GreetingsEditor from 'shared/components/GreetingsEditor.vue';
import ConfigurationPage from './settingsPage/ConfigurationPage.vue';
import VoiceConfigurationPage from './settingsPage/VoiceConfigurationPage.vue';
import WhatsappCallingPage from './settingsPage/WhatsappCallingPage.vue';
import CustomerSatisfactionPage from './settingsPage/CustomerSatisfactionPage.vue';
import WhatsappTemplatesPage from './settingsPage/WhatsappTemplatesPage.vue';
import CollaboratorsPage from './settingsPage/CollaboratorsPage.vue';
import BotConfiguration from './components/BotConfiguration.vue';
import AccountHealth from './components/AccountHealth.vue';
import EvolutionApi from './components/EvolutionApi.vue';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import SenderNameExamplePreview from './components/SenderNameExamplePreview.vue';
import LockToSingleConversationPreview from './components/LockToSingleConversationPreview.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SpinnerLoader from 'dashboard/components-next/spinner/Spinner.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Widget from 'dashboard/modules/widget-preview/components/Widget.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

export default {
  components: {
    BotConfiguration,
    CollaboratorsPage,
    ConfigurationPage,
    VoiceConfigurationPage,
    WhatsappCallingPage,
    WhatsappTemplatesPage,
    CustomerSatisfactionPage,
    FacebookReauthorize,
    GreetingsEditor,
    PreChatFormSettings,
    SettingIntroBanner,
    SettingsToggleSection,
    SettingsFieldSection,
    SettingsAccordion,
    WeeklyAvailability,
    SenderNameExamplePreview,
    LockToSingleConversationPreview,
    MicrosoftReauthorize,
    GoogleReauthorize,
    Icon,
    NextButton,
    SpinnerLoader,
    InstagramReauthorize,
    TiktokReauthorize,
    WhatsappReauthorize,
    DuplicateInboxBanner,
    Editor,
    Avatar,
    ColorPicker,
    SelectInput,
    AccountHealth,
    EvolutionApi,
    Widget,
    AccessToken,
  },
  mixins: [inboxMixin],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      avatarFile: null,
      avatarUrl: '',
      greetingEnabled: true,
      greetingMessage: '',
      emailCollectEnabled: false,
      senderNameType: 'friendly',
      businessName: '',
      locktoSingleConversation: false,
      allowMessagesAfterResolved: true,
      continuityViaEmail: true,
      selectedInboxName: '',
      channelWebsiteUrl: '',
      webhookUrl: '',
      channelWelcomeTitle: '',
      channelWelcomeTagline: '',
      selectedFeatureFlags: [],
      replyTime: '',
      selectedTabIndex: 0,
      isLoadingInbox: true,
      selectedPortalSlug: '',
      showBusinessNameInput: false,
      healthData: null,
      isLoadingHealth: false,
      healthError: null,
      isRegisteringWebhook: false,
      isSyncingMetaAvatar: false,
      widgetBubblePosition: 'right',
      widgetBubbleType: 'standard',
      widgetBubbleLauncherTitle: '',
      widgetLayout: 'standard',
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isOnTalkviaCloud: 'globalConfig/isOnTalkviaCloud',
      uiFlags: 'inboxes/getUIFlags',
      portals: 'portals/allPortals',
    }),
    isInboundEmailEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.INBOUND_EMAILS
      );
    },
    showContinuityToggle() {
      if (this.isInboundEmailEnabled) return true;
      return this.isOnTalkviaCloud;
    },
    isContinuityDisabled() {
      return this.isOnTalkviaCloud && !this.isInboundEmailEnabled;
    },
    continuityDescription() {
      if (this.isContinuityDisabled) {
        return this.$t(
          'INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL_DISABLED_TEXT'
        );
      }
      return this.$t(
        'INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL_SUB_TEXT'
      );
    },
    selectedTabKey() {
      return this.tabs[this.selectedTabIndex]?.key;
    },
    shouldShowWhatsAppConfiguration() {
      return this.isAWhatsAppCloudChannel;
    },
    whatsAppAPIProviderName() {
      if (this.isAWhatsAppCloudChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD');
      }
      if (this.is360DialogWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.360_DIALOG');
      }
      if (this.isATwilioWhatsAppChannel) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO');
      }
      return '';
    },
    tabs() {
      let visibleToAllChannelTabs = [
        {
          key: 'inbox-settings',
          name: this.$t('INBOX_MGMT.TABS.SETTINGS'),
        },
        {
          key: 'collaborators',
          name: this.$t('INBOX_MGMT.TABS.COLLABORATORS'),
        },
      ];

      visibleToAllChannelTabs = [
        ...visibleToAllChannelTabs,
        {
          key: 'business-hours',
          name: this.$t('INBOX_MGMT.TABS.BUSINESS_HOURS'),
        },
        {
          key: 'csat',
          name: this.$t('INBOX_MGMT.TABS.CSAT'),
        },
      ];

      if (this.isAWebWidgetInbox) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'pre-chat-form',
            name: this.$t('INBOX_MGMT.TABS.PRE_CHAT_FORM'),
          },
        ];
      }

      if (
        this.isATwilioChannel ||
        this.isALineChannel ||
        this.isAPIInbox ||
        (this.isAnEmailChannel && !this.inbox.provider) ||
        this.shouldShowWhatsAppConfiguration ||
        this.isAWebWidgetInbox
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'configuration',
            name: this.$t('INBOX_MGMT.TABS.CONFIGURATION'),
          },
        ];
      }

      if (
        this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.AGENT_BOTS)
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'bot-configuration',
            name: this.$t('INBOX_MGMT.TABS.BOT_CONFIGURATION'),
          },
        ];
      }
      if (this.shouldShowWhatsAppConfiguration) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'whatsapp-health',
            name: this.$t('INBOX_MGMT.TABS.ACCOUNT_HEALTH'),
          },
          {
            key: 'whatsapp-templates',
            name: this.$t('INBOX_MGMT.TABS.WHATSAPP_TEMPLATES'),
          },
        ];
      }

      if (
        this.isATwilioChannel &&
        this.inbox.phone_number &&
        this.inbox.medium === 'sms' &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.CHANNEL_VOICE
        )
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'voice-configuration',
            name: this.$t('INBOX_MGMT.TABS.VOICE'),
          },
        ];
      }

      if (
        this.isAPIInbox &&
        this.inbox.additional_attributes?.is_evolution_api_integration
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'qr-code',
            name: this.$t('INBOX_MGMT.ADD.EVOLUTION_API_SETTINGS.TITLE'),
          },
        ];
      }

      if (
        this.isAWhatsAppCloudChannel &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.CHANNEL_VOICE
        )
      ) {
        visibleToAllChannelTabs = [
          ...visibleToAllChannelTabs,
          {
            key: 'calls-configuration',
            name: this.$t('INBOX_MGMT.TABS.CALLS'),
          },
        ];
      }

      return visibleToAllChannelTabs;
    },
    currentInboxId() {
      return this.$route.params.inboxId;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentInboxId);
    },
    inboxIcon() {
      const {
        medium,
        channel_type: type,
        voice_enabled: voiceEnabled,
      } = this.inbox;
      return getInboxIconByType(type, medium, 'line', voiceEnabled);
    },
    bannerMaxWidth() {
      const narrowTabs = ['collaborators', 'bot-configuration'];
      const wideIfWebWidget = ['configuration', 'inbox-settings'];
      if (narrowTabs.includes(this.selectedTabKey)) return 'max-w-4xl';
      if (wideIfWebWidget.includes(this.selectedTabKey)) {
        return this.isAWebWidgetInbox ? 'max-w-7xl' : 'max-w-4xl';
      }
      return 'max-w-7xl';
    },
    inboxName() {
      if (this.isATwilioSMSChannel || this.isATwilioWhatsAppChannel) {
        return `${this.inbox.name} (${
          this.inbox.messaging_service_sid || this.inbox.phone_number
        })`;
      }
      if (this.isAWhatsAppChannel) {
        return `${this.inbox.name} (${this.inbox.phone_number})`;
      }
      if (this.isAnEmailChannel) {
        return `${this.inbox.name} (${this.inbox.email})`;
      }
      return this.inbox.name;
    },
    canLocktoSingleConversation() {
      return (
        this.isASmsInbox ||
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAPIInbox ||
        this.isAnInstagramChannel ||
        this.isALineChannel ||
        this.isATiktokChannel ||
        this.isATelegramChannel
      );
    },
    inboxNameLabel() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.LABEL');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.LABEL');
    },
    inboxNamePlaceHolder() {
      if (this.isAWebWidgetInbox) {
        return this.$t('INBOX_MGMT.ADD.WEBSITE_NAME.PLACEHOLDER');
      }
      return this.$t('INBOX_MGMT.ADD.CHANNEL_NAME.PLACEHOLDER');
    },
    textAreaChannels() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return true;
      return false;
    },
    instagramUnauthorized() {
      return this.isAnInstagramChannel && this.inbox.reauthorization_required;
    },
    tiktokUnauthorized() {
      return this.isATiktokChannel && this.inbox.reauthorization_required;
    },
    // Check if a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return this.inbox.channel_type === INBOX_TYPES.FB && instagramInbox;
    },
    microsoftUnauthorized() {
      return this.isAMicrosoftInbox && this.inbox.reauthorization_required;
    },
    facebookUnauthorized() {
      return this.isAFacebookInbox && this.inbox.reauthorization_required;
    },
    googleUnauthorized() {
      const isLegacyInbox = ['imap.gmail.com', 'imap.google.com'].includes(
        this.inbox.imap_address
      );

      return (
        (this.isAGoogleInbox || isLegacyInbox) &&
        this.inbox.reauthorization_required
      );
    },
    isEmbeddedSignupWhatsApp() {
      return this.inbox.provider_config?.source === 'embedded_signup';
    },
    whatsappUnauthorized() {
      return (
        this.isAWhatsAppCloudChannel &&
        this.isEmbeddedSignupWhatsApp &&
        (!this.isOnTalkviaCloud ||
          this.isFeatureEnabledonAccount(
            this.accountId,
            FEATURE_FLAGS.WHATSAPP_EMBEDDED_SIGNUP_FLOW
          )) &&
        this.inbox.reauthorization_required
      );
    },
    whatsappRegistrationIncomplete() {
      if (
        !this.healthData ||
        !this.isAWhatsAppCloudChannel ||
        !this.isEmbeddedSignupWhatsApp
      ) {
        return false;
      }

      return (
        this.healthData.platform_type === 'NOT_APPLICABLE' ||
        this.healthData.throughput?.level === 'NOT_APPLICABLE'
      );
    },
    showWhatsAppManualMigration() {
      return (
        this.isAWhatsAppCloudChannel &&
        this.isEmbeddedSignupWhatsApp &&
        this.healthError?.type !== 'authorization' &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.WHATSAPP_MANUAL_TRANSFER
        )
      );
    },
    widgetBuilderStorageKey() {
      return `${LOCAL_STORAGE_KEYS.WIDGET_BUILDER}${this.inbox.id}`;
    },
  },
  watch: {
    $route(to, from) {
      if (to.name === 'settings_inbox_show') {
        const inboxChanged = to.params.inboxId !== from.params.inboxId;
        if (inboxChanged) {
          this.syncInboxData();
          this.setTabFromRouteParam();
          this.openWhatsAppManualMigrationIfRequested();
        }
      }
    },
    inbox: {
      handler(newInbox, oldInbox) {
        if (newInbox?.id !== oldInbox?.id) {
          this.syncInboxData();
          this.fetchHealthData();
          this.$nextTick(() => {
            this.setTabFromRouteParam();
            this.openWhatsAppManualMigrationIfRequested();
          });
        } else {
          this.selectedFeatureFlags = newInbox?.selected_feature_flags || [];
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    this.fetchSharedData();

    try {
      // Do not depend on the cached request started by the sidebar. If the
      // browser cache/IndexedDB stalls, its global flag can remain active.
      await this.$store.dispatch('inboxes/getFromNetwork');
    } catch {
      useAlert(this.$t('INBOX_MGMT.LIST.FETCH_ERROR'));
    } finally {
      this.isLoadingInbox = false;
    }
  },
  methods: {
    openWhatsAppManualMigrationDialog() {
      this.$refs.whatsappManualMigrationDialog?.open();
    },
    openWhatsAppManualMigrationIfRequested() {
      if (
        this.showWhatsAppManualMigration &&
        this.$route.query.migration === 'whatsapp_manual'
      ) {
        this.$nextTick(() => {
          this.openWhatsAppManualMigrationDialog();
        });
      }
    },
    async transferWhatsAppToManualSetup(form) {
      this.isTransferringWhatsAppToManual = true;
      try {
        const providerConfig = { ...(this.inbox.provider_config || {}) };
        delete providerConfig.source;
        const payload = {
          id: this.inbox.id,
          formData: false,
          channel: {
            provider_config: {
              ...providerConfig,
              phone_number_id: form.phoneNumberId,
              business_account_id: form.wabaId,
              api_key: form.accessToken,
            },
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MANUAL_TRANSFER_SUCCESS')
        );
        this.$refs.whatsappManualMigrationDialog?.close();
      } catch (error) {
        useAlert(
          this.$t('INBOX_MGMT.SETTINGS_POPUP.WHATSAPP_MANUAL_TRANSFER_ERROR')
        );
      } finally {
        this.isTransferringWhatsAppToManual = false;
      }
    },
    async copyWebhookSecret(value) {
      await copyTextToClipboard(value);
      useAlert(
        this.$t(
          'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_SECRET.COPY_SUCCESS'
        )
      );
    },
    async resetWebhookSecret() {
      const response = await this.$store.dispatch(
        'inboxes/resetSecret',
        this.inbox.id
      );
      if (response) {
        useAlert(
          this.$t(
            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_SECRET.RESET_SUCCESS'
          )
        );
      } else {
        useAlert(
          this.$t(
            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_SECRET.RESET_ERROR'
          )
        );
      }
    },
    fetchSharedData() {
      this.$store.dispatch('agents/get');
      this.$store.dispatch('teams/get');
      this.$store.dispatch('labels/get');
      this.$store.dispatch('portals/index');
    },
    syncInboxData() {
      if (!this.inbox || !this.inbox.id) return;

      this.avatarUrl = this.inbox.avatar_url;
      this.selectedInboxName = this.inbox.name;
      this.webhookUrl = this.inbox.webhook_url;
      this.greetingEnabled = this.inbox.greeting_enabled || false;
      this.greetingMessage = this.inbox.greeting_message || '';
      this.emailCollectEnabled = this.inbox.enable_email_collect;
      this.senderNameType = this.inbox.sender_name_type;
      this.businessName = this.inbox.business_name;
      this.allowMessagesAfterResolved =
        this.inbox.allow_messages_after_resolved;
      this.continuityViaEmail = this.inbox.continuity_via_email;
      this.channelWebsiteUrl = this.inbox.website_url;
      this.channelWelcomeTitle = this.inbox.welcome_title;
      this.channelWelcomeTagline = this.inbox.welcome_tagline || '';
      this.selectedFeatureFlags = this.inbox.selected_feature_flags || [];
      this.replyTime = this.inbox.reply_time;
      this.widgetLayout = this.inbox.widget_layout || 'standard';
      this.locktoSingleConversation = this.inbox.lock_to_single_conversation;
      this.selectedPortalSlug = this.inbox.help_center
        ? this.inbox.help_center.slug
        : '';

      const savedBubbleSettings = LocalStorage.get(
        this.widgetBuilderStorageKey
      );
      if (savedBubbleSettings) {
        this.widgetBubblePosition = savedBubbleSettings.position || 'right';
        this.widgetBubbleType = savedBubbleSettings.type || 'standard';
        this.widgetBubbleLauncherTitle =
          savedBubbleSettings.launcherTitle || '';
      } else {
        this.widgetBubblePosition = 'right';
        this.widgetBubbleType = 'standard';
        this.widgetBubbleLauncherTitle = '';
      }
    },
    async fetchHealthData() {
      if (!this.inbox) return;

      if (!this.isAWhatsAppCloudChannel) {
        return;
      }

      try {
        this.isLoadingHealth = true;
        this.healthError = null;
        const response = await InboxHealthAPI.getHealthStatus(this.inbox.id);
        this.healthData = response.data;
      } catch (error) {
        const apiError = error.response?.data?.error;
        this.healthError =
          typeof apiError === 'object'
            ? apiError
            : {
                type: 'generic',
                message: apiError || error.message,
              };
      } finally {
        this.isLoadingHealth = false;
      }
    },
    goToWhatsAppConfiguration() {
      const configurationTabIndex = this.tabs.findIndex(
        tab => tab.key === 'configuration'
      );
      if (configurationTabIndex !== -1) {
        this.onTabChange(configurationTabIndex);
      }
    },
    async registerWebhook({ repairCoexistence = false } = {}) {
      if (!this.inbox) return;

      try {
        this.isRegisteringWebhook = true;
        await InboxHealthAPI.registerWebhook(this.inbox.id);
        const successMessage = repairCoexistence
          ? this.$t(
              'INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.REPAIR_COEXISTENCE_SUCCESS'
            )
          : this.$t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.REGISTER_SUCCESS');
        useAlert(successMessage);
        await this.fetchHealthData();
      } catch (error) {
        const fallbackMessage = repairCoexistence
          ? this.$t(
              'INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.REPAIR_COEXISTENCE_ERROR'
            )
          : this.$t('INBOX_MGMT.ACCOUNT_HEALTH.WEBHOOK.REGISTER_ERROR');
        useAlert(error.message || fallbackMessage);
      } finally {
        this.isRegisteringWebhook = false;
      }
    },
    async syncWhatsAppCloudAvatar() {
      if (!this.isAWhatsAppCloudChannel) return;

      try {
        this.isSyncingMetaAvatar = true;
        const response = await this.$store.dispatch(
          'inboxes/syncWhatsAppProfileAvatar',
          this.currentInboxId
        );

        if (response?.avatar_url) {
          this.avatarUrl = response.avatar_url;
        }

        useAlert(response?.message || 'Foto sincronizada com sucesso.');
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message;
        useAlert(
          errorMessage || 'Nao foi possivel sincronizar a foto da Meta.'
        );
      } finally {
        this.isSyncingMetaAvatar = false;
      }
    },
    handleFeatureFlag(e) {
      this.selectedFeatureFlags = this.toggleInput(
        this.selectedFeatureFlags,
        e.target.value
      );
    },
    toggleInput(selected, current) {
      if (selected.includes(current)) {
        const newSelectedFlags = selected.filter(flag => flag !== current);
        return newSelectedFlags;
      }
      return [...selected, current];
    },
    onTabChange(selectedTabIndex) {
      this.selectedTabIndex = selectedTabIndex;
      this.updateRouteWithoutRefresh(selectedTabIndex);
    },
    tabIcon(tabKey) {
      const iconMap = {
        'inbox-settings': 'i-lucide-settings',
        collaborators: 'i-lucide-users',
        'business-hours': 'i-lucide-clock',
        csat: 'i-lucide-star',
        'pre-chat-form': 'i-lucide-clipboard-list',
        configuration: 'i-lucide-sliders-horizontal',
        'bot-configuration': 'i-lucide-bot',
        'whatsapp-health': 'i-lucide-heart-pulse',
        'whatsapp-templates': 'i-lucide-message-square-text',
        'voice-configuration': 'i-lucide-phone-call',
        'calls-configuration': 'i-lucide-phone',
        'qr-code': 'i-lucide-qr-code',
      };
      return iconMap[tabKey] || 'i-lucide-circle';
    },
    updateRouteWithoutRefresh(selectedTabIndex) {
      const tab = this.tabs[selectedTabIndex];
      if (!tab) return;

      const { accountId, inboxId } = this.$route.params;
      const baseUrl = `/app/accounts/${accountId}/settings/inboxes/${inboxId}`;

      // Append the tab key only if it's not the default.
      const newUrl =
        tab.key === 'inbox-settings' ? baseUrl : `${baseUrl}/${tab.key}`;
      // Update URL without triggering route watcher
      window.history.replaceState(null, '', newUrl);
    },
    setTabFromRouteParam() {
      const { tab: tabParam } = this.$route.params;
      if (!tabParam) {
        this.selectedTabIndex = 0;
        return;
      }
      const normalizedTabParam =
        tabParam === 'evolution-api' ? 'qr-code' : tabParam;
      const tabIndex = this.tabs.findIndex(
        tab => tab.key === normalizedTabParam
      );
      this.selectedTabIndex = tabIndex === -1 ? 0 : tabIndex;
      if (tabParam !== normalizedTabParam && tabIndex !== -1) {
        this.updateRouteWithoutRefresh(tabIndex);
      }
    },
    async updateInbox() {
      const bubbleSettings = {
        position: this.widgetBubblePosition,
        type: this.widgetBubbleType,
        launcherTitle: this.widgetBubbleLauncherTitle,
      };
      LocalStorage.set(this.widgetBuilderStorageKey, bubbleSettings);

      try {
        const payload = {
          id: this.currentInboxId,
          name: this.selectedInboxName?.trim(),
          enable_email_collect: this.emailCollectEnabled,
          allow_messages_after_resolved: this.allowMessagesAfterResolved,
          greeting_enabled: this.greetingEnabled,
          greeting_message: this.greetingMessage || '',
          portal_id: this.selectedPortalSlug
            ? this.portals.find(
                portal => portal.slug === this.selectedPortalSlug
              )?.id || null
            : null,
          lock_to_single_conversation: this.locktoSingleConversation,
          sender_name_type: this.senderNameType,
          business_name: this.businessName || null,
          channel: {
            widget_color: this.inbox.widget_color,
            widget_layout: this.widgetLayout,
            website_url: this.channelWebsiteUrl,
            webhook_url: this.webhookUrl,
            welcome_title: this.channelWelcomeTitle || '',
            welcome_tagline: this.channelWelcomeTagline || '',
            selectedFeatureFlags: this.selectedFeatureFlags,
            reply_time: this.replyTime || 'in_a_few_minutes',
            continuity_via_email:
              this.isInboundEmailEnabled && this.continuityViaEmail,
          },
        };
        if (this.avatarFile) {
          payload.avatar = this.avatarFile;
        }
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.showBusinessNameInput = false;
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
    },
    handleImageUpload({ file, url }) {
      this.avatarFile = file;
      this.avatarUrl = url;
    },
    async handleAvatarDelete() {
      try {
        await this.$store.dispatch(
          'inboxes/deleteInboxAvatar',
          this.currentInboxId
        );
        this.avatarFile = null;
        this.avatarUrl = '';
        useAlert(this.$t('INBOX_MGMT.DELETE.API.AVATAR_SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('INBOX_MGMT.DELETE.API.AVATAR_ERROR_MESSAGE')
        );
      }
    },
    toggleSenderNameType(key) {
      this.senderNameType = key;
    },
    onClickShowBusinessNameInput() {
      this.showBusinessNameInput = true;
      this.$nextTick(() => {
        this.$refs.businessNameInput?.focus();
      });
    },
    hideBusinessNameInput() {
      this.showBusinessNameInput = false;
    },
    toggleLockToSingleConversation(value) {
      this.locktoSingleConversation = value;
    },
  },
  validations: {
    webhookUrl: {
      shouldBeUrl,
    },
    selectedInboxName: {},
  },
};
</script>

<template>
  <div
    v-if="isLoadingInbox"
    class="flex items-center justify-center h-full w-full"
  >
    <SpinnerLoader :size="28" class="text-n-blue-9" />
  </div>
  <div
    v-else
    class="grid grid-rows-[auto_1fr] h-full flex-grow flex-shrink pr-0 pl-0 w-full min-w-0 settings"
  >
    <SettingIntroBanner
      :header-image="inbox.avatarUrl"
      :header-title="inboxName"
    />
    <section class="w-full overflow-auto py-8">
      <div class="mx-auto w-full max-w-[1680px] px-4 lg:px-8 xl:px-10">
        <div
          class="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start"
        >
          <aside
            class="sticky top-0 z-20 min-w-0 -mx-4 px-4 py-3 border-b border-n-weak bg-n-solid-1/95 backdrop-blur lg:top-4 lg:z-10 lg:mx-0 lg:self-start lg:border-0 lg:bg-transparent lg:p-0 lg:backdrop-blur-none"
          >
            <div class="relative lg:hidden">
              <Icon
                :icon="tabIcon(selectedTabKey)"
                class="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-n-slate-10"
              />
              <select
                :value="selectedTabIndex"
                :aria-label="$t('INBOX_MGMT.HEADER')"
                class="!mb-0 h-11 w-full appearance-none rounded-lg border border-n-weak bg-n-solid-2 !pl-10 !pr-10 text-sm font-medium text-n-slate-12 shadow-sm"
                @change="onTabChange(Number($event.target.value))"
              >
                <option
                  v-for="(tab, index) in tabs"
                  :key="tab.key"
                  :value="index"
                >
                  {{ tab.name }}
                </option>
              </select>
              <Icon
                icon="i-lucide-chevron-down"
                class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-10"
              />
            </div>

            <nav class="hidden lg:flex lg:flex-col lg:items-stretch lg:gap-1.5">
              <button
                v-for="(tab, index) in tabs"
                :key="tab.key"
                type="button"
                class="group flex shrink-0 items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium whitespace-nowrap transition lg:w-full lg:rounded-lg lg:whitespace-normal"
                :class="
                  selectedTabIndex === index
                    ? 'bg-n-surface-1 text-n-blue-11 shadow-sm'
                    : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
                "
                @click="onTabChange(index)"
              >
                <Icon
                  :icon="tabIcon(tab.key)"
                  class="size-4 shrink-0"
                  :class="
                    selectedTabIndex === index
                      ? 'text-n-blue-11'
                      : 'text-n-slate-10 group-hover:text-n-slate-12'
                  "
                />
                <span class="leading-5">{{ tab.name }}</span>
              </button>
            </nav>
          </aside>

          <div class="min-w-0">
            <MicrosoftReauthorize
              v-if="microsoftUnauthorized"
              :inbox="inbox"
              class="mb-4"
              :class="bannerMaxWidth"
            />
            <FacebookReauthorize
              v-if="facebookUnauthorized"
              :inbox="inbox"
              class="mb-4"
              :class="bannerMaxWidth"
            />
            <GoogleReauthorize
              v-if="googleUnauthorized"
              :inbox="inbox"
              class="mb-4"
              :class="bannerMaxWidth"
            />
            <InstagramReauthorize
              v-if="instagramUnauthorized"
              :inbox="inbox"
              class="mb-4"
              :class="bannerMaxWidth"
            />
            <TiktokReauthorize
              v-if="tiktokUnauthorized"
              :inbox="inbox"
              class="mb-4"
              :class="bannerMaxWidth"
            />
            <WhatsappReauthorize
              v-if="whatsappUnauthorized"
              :whatsapp-registration-incomplete="whatsappRegistrationIncomplete"
              :inbox="inbox"
              class="mb-4"
              :class="bannerMaxWidth"
            />
            <DuplicateInboxBanner
              v-if="hasDuplicateInstagramInbox"
              :content="$t('INBOX_MGMT.ADD.INSTAGRAM.DUPLICATE_INBOX_BANNER')"
              class="mb-4"
              :class="bannerMaxWidth"
            />

            <div
              v-if="selectedTabKey === 'inbox-settings'"
              class="flex flex-col md:flex-row items-center lg:items-start justify-between gap-5 lg:gap-10 mx-6"
            >
              <div
                class="flex-1 flex flex-col min-w-0"
                :class="{
                  'max-w-2xl': isAWebWidgetInbox,
                  'max-w-4xl': !isAWebWidgetInbox,
                }"
              >
                <div class="flex flex-col gap-1 items-start mb-4">
                  <label class="text-heading-3 text-n-slate-12">
                    {{
                      $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_AVATAR.LABEL')
                    }}
                  </label>
                  <Avatar
                    :src="avatarUrl"
                    :size="64"
                    :icon-name="inboxIcon"
                    name=""
                    allow-upload
                    rounded-full
                    @upload="handleImageUpload"
                    @delete="handleAvatarDelete"
                  />
                  <NextButton
                    v-if="isAWhatsAppCloudChannel"
                    type="button"
                    sm
                    outline
                    blue
                    icon="i-lucide-refresh-cw"
                    :label="$t('INBOX_MGMT.SYNC_META_AVATAR')"
                    :is-loading="isSyncingMetaAvatar"
                    :disabled="isSyncingMetaAvatar"
                    class="mt-2"
                    @click="syncWhatsAppCloudAvatar"
                  />
                </div>
                <SettingsFieldSection :label="inboxNameLabel">
                  <woot-input
                    v-model="selectedInboxName"
                    class="[&>input]:!mb-0"
                    :class="{ error: v$.selectedInboxName.$error }"
                    :placeholder="inboxNamePlaceHolder"
                    :error="
                      v$.selectedInboxName.$error
                        ? $t('INBOX_MGMT.ADD.CHANNEL_NAME.ERROR')
                        : ''
                    "
                    @blur="v$.selectedInboxName.$touch"
                  />
                </SettingsFieldSection>
                <SettingsFieldSection
                  v-if="
                    isAPIInbox &&
                    !inbox.additional_attributes?.is_evolution_api_integration
                  "
                  :label="
                    $t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.LABEL'
                    )
                  "
                >
                  <woot-input
                    v-model="webhookUrl"
                    class="[&>input]:!mb-0"
                    :class="{ error: v$.webhookUrl.$error }"
                    :placeholder="
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.PLACEHOLDER'
                      )
                    "
                    :error="
                      v$.webhookUrl.$error
                        ? $t(
                            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_URL.ERROR'
                          )
                        : ''
                    "
                    @blur="v$.webhookUrl.$touch"
                  />
                </SettingsFieldSection>

                <SettingsFieldSection
                  v-if="isAPIInbox && inbox.secret"
                  :label="
                    $t(
                      'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WEBHOOK_SECRET.LABEL'
                    )
                  "
                >
                  <AccessToken
                    :value="inbox.secret"
                    @on-copy="copyWebhookSecret"
                    @on-reset="resetWebhookSecret"
                  />
                </SettingsFieldSection>

                <SettingsFieldSection
                  v-if="isAWebWidgetInbox"
                  :label="
                    $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.LABEL')
                  "
                >
                  <woot-input
                    v-model="channelWebsiteUrl"
                    class="[&>input]:!mb-0"
                    :placeholder="
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_DOMAIN.PLACEHOLDER'
                      )
                    "
                  />
                </SettingsFieldSection>

                <SettingsFieldSection
                  v-if="isAWhatsAppChannel"
                  :label="$t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.LABEL')"
                >
                  <input
                    v-model="whatsAppAPIProviderName"
                    type="text"
                    disabled
                    class="!mb-0"
                  />
                </SettingsFieldSection>

                <SettingsFieldSection
                  :label="$t('INBOX_MGMT.HELP_CENTER.LABEL')"
                  :help-text="$t('INBOX_MGMT.HELP_CENTER.SUB_TEXT')"
                >
                  <SelectInput
                    v-model="selectedPortalSlug"
                    :placeholder="$t('INBOX_MGMT.HELP_CENTER.PLACEHOLDER')"
                    :options="[
                      { value: '', label: $t('INBOX_MGMT.HELP_CENTER.NONE') },
                      ...portals.map(p => ({ value: p.slug, label: p.name })),
                    ]"
                  />
                </SettingsFieldSection>

                <SettingsFieldSection
                  v-if="canLocktoSingleConversation"
                  :label="
                    $t('INBOX_MGMT.SETTINGS_POPUP.LOCK_TO_SINGLE_CONVERSATION')
                  "
                  class="[&>div>div]:justify-end [&>div>div]:flex lg:[&>div:first-child]:h-12 [&>div:first-child]:h-16"
                >
                  <template #extra>
                    <LockToSingleConversationPreview
                      :lock-to-single-conversation="locktoSingleConversation"
                      @update="toggleLockToSingleConversation"
                    />
                  </template>
                </SettingsFieldSection>

                <SettingsFieldSection
                  v-if="isAWebWidgetInbox || isAnEmailChannel"
                  :label="$t('INBOX_MGMT.EDIT.SENDER_NAME_SECTION.TITLE')"
                  class="[&>div>div]:justify-end [&>div>div]:flex lg:[&>div:first-child]:h-12 [&>div:first-child]:h-16"
                >
                  <NextButton
                    v-if="!showBusinessNameInput"
                    ghost
                    blue
                    sm
                    :label="
                      $t(
                        'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.BUTTON_TEXT'
                      )
                    "
                    @click="onClickShowBusinessNameInput"
                  />

                  <div
                    v-if="showBusinessNameInput"
                    v-on-clickaway="hideBusinessNameInput"
                    class="flex justify-end gap-2 w-full"
                  >
                    <input
                      ref="businessNameInput"
                      v-model="businessName"
                      :placeholder="
                        $t(
                          'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.PLACEHOLDER'
                        )
                      "
                      class="!mb-0"
                      type="text"
                    />
                    <NextButton
                      :label="
                        $t(
                          'INBOX_MGMT.EDIT.SENDER_NAME_SECTION.BUSINESS_NAME.SAVE_BUTTON_TEXT'
                        )
                      "
                      class="flex-shrink-0"
                      @click="updateInbox"
                    />
                  </div>

                  <template #extra>
                    <SenderNameExamplePreview
                      :sender-name-type="senderNameType"
                      :business-name="businessName"
                      :is-website-channel="isAWebWidgetInbox"
                      @update="toggleSenderNameType"
                    />
                  </template>
                </SettingsFieldSection>

                <SettingsAccordion
                  v-if="isAWebWidgetInbox"
                  :title="$t('INBOX_MGMT.WIDGET_FEATURES')"
                  class="mt-6"
                >
                  <SettingsFieldSection
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_LAYOUT.LABEL'
                      )
                    "
                    :help-text="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_LAYOUT.HELP_TEXT'
                      )
                    "
                  >
                    <SelectInput
                      v-model="widgetLayout"
                      :options="[
                        {
                          label: $t(
                            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_LAYOUT.STANDARD'
                          ),
                          value: 'standard',
                        },
                        {
                          label: $t(
                            'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_LAYOUT.ALWAYS_OPEN'
                          ),
                          value: 'always_open',
                        },
                      ]"
                      class="[&>select]:!p-0 min-w-48 [&>select]:!outline-none"
                    />
                  </SettingsFieldSection>

                  <SettingsFieldSection
                    :label="
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.LABEL'
                      )
                    "
                  >
                    <woot-input
                      v-model="channelWelcomeTitle"
                      class="[&>input]:!mb-0"
                      :placeholder="
                        $t(
                          'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TITLE.PLACEHOLDER'
                        )
                      "
                    />
                  </SettingsFieldSection>

                  <SettingsFieldSection
                    :label="
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.LABEL'
                      )
                    "
                    class="[&>div]:!items-start [&>div>label]:mt-1"
                  >
                    <Editor
                      v-model="channelWelcomeTagline"
                      :placeholder="
                        $t(
                          'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_WELCOME_TAGLINE.PLACEHOLDER'
                        )
                      "
                      :max-length="255"
                      channel-type="Context::InboxSettings"
                    />
                  </SettingsFieldSection>

                  <SettingsFieldSection
                    :label="
                      $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.WIDGET_COLOR.LABEL')
                    "
                  >
                    <div class="justify-start">
                      <ColorPicker v-model="inbox.widget_color" />
                    </div>
                  </SettingsFieldSection>
                  <SettingsFieldSection
                    v-if="widgetLayout === 'standard'"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE'
                      )
                    "
                  >
                    <div class="flex items-center gap-6">
                      <div class="flex items-center gap-2">
                        <label class="text-n-slate-11 text-heading-3">
                          {{
                            $t(
                              'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION_LABEL'
                            )
                          }}
                        </label>
                        <SelectInput
                          v-model="widgetBubblePosition"
                          :options="[
                            {
                              label: $t(
                                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.LEFT'
                              ),
                              value: 'left',
                            },
                            {
                              label: $t(
                                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_POSITION.RIGHT'
                              ),
                              value: 'right',
                            },
                          ]"
                          class="[&>select]:!p-0 min-w-16 [&>select]:!outline-none"
                        />
                      </div>
                      <div class="h-3 w-px bg-n-weak rounded-lg" />
                      <div class="flex items-center gap-2">
                        <label class="text-n-slate-11 text-heading-3">
                          {{
                            $t(
                              'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE_LABEL'
                            )
                          }}
                        </label>
                        <SelectInput
                          v-model="widgetBubbleType"
                          :options="[
                            {
                              label: $t(
                                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.STANDARD'
                              ),
                              value: 'standard',
                            },
                            {
                              label: $t(
                                'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_TYPE.EXPANDED_BUBBLE'
                              ),
                              value: 'expanded_bubble',
                            },
                          ]"
                          class="[&>select]:!p-0 min-w-16 [&>select]:!outline-none"
                        />
                      </div>
                    </div>
                  </SettingsFieldSection>

                  <SettingsFieldSection
                    v-if="widgetLayout === 'standard'"
                    :label="
                      $t(
                        'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.LABEL'
                      )
                    "
                  >
                    <woot-input
                      v-model="widgetBubbleLauncherTitle"
                      :placeholder="
                        $t(
                          'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_BUBBLE_LAUNCHER_TITLE.PLACE_HOLDER'
                        )
                      "
                      class="[&>input]:!mb-0"
                    />
                  </SettingsFieldSection>
                  <SettingsFieldSection
                    :label="
                      $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.TITLE')
                    "
                    :help-text="
                      $t('INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.HELP_TEXT')
                    "
                  >
                    <SelectInput
                      v-model="replyTime"
                      :options="[
                        {
                          value: 'in_a_few_minutes',
                          label: $t(
                            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_FEW_MINUTES'
                          ),
                        },
                        {
                          value: 'in_a_few_hours',
                          label: $t(
                            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_FEW_HOURS'
                          ),
                        },
                        {
                          value: 'in_a_day',
                          label: $t(
                            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.REPLY_TIME.IN_A_DAY'
                          ),
                        },
                      ]"
                    />
                  </SettingsFieldSection>

                  <SettingsFieldSection
                    :label="$t('INBOX_MGMT.FEATURES.LABEL')"
                    class="[&>div]:!items-start [&>div>label]:mt-2"
                  >
                    <div class="flex flex-col gap-1 items-start">
                      <div class="flex gap-2 pt-2 py-0.5">
                        <input
                          v-model="selectedFeatureFlags"
                          type="checkbox"
                          value="attachments"
                          @input="handleFeatureFlag"
                        />
                        <label for="attachments">
                          {{ $t('INBOX_MGMT.FEATURES.DISPLAY_FILE_PICKER') }}
                        </label>
                      </div>
                      <div class="flex gap-2 py-0.5">
                        <input
                          v-model="selectedFeatureFlags"
                          type="checkbox"
                          value="emoji_picker"
                          @input="handleFeatureFlag"
                        />
                        <label for="emoji_picker">
                          {{ $t('INBOX_MGMT.FEATURES.DISPLAY_EMOJI_PICKER') }}
                        </label>
                      </div>
                      <div class="flex gap-2 py-0.5">
                        <input
                          v-model="selectedFeatureFlags"
                          type="checkbox"
                          value="end_conversation"
                          @input="handleFeatureFlag"
                        />
                        <label for="end_conversation">
                          {{ $t('INBOX_MGMT.FEATURES.ALLOW_END_CONVERSATION') }}
                        </label>
                      </div>
                      <div class="flex gap-2 py-0.5">
                        <input
                          v-model="selectedFeatureFlags"
                          type="checkbox"
                          value="use_inbox_avatar_for_bot"
                          @input="handleFeatureFlag"
                        />
                        <label for="use_inbox_avatar_for_bot">
                          {{
                            $t('INBOX_MGMT.FEATURES.USE_INBOX_AVATAR_FOR_BOT')
                          }}
                        </label>
                      </div>
                    </div>
                  </SettingsFieldSection>
                </SettingsAccordion>

                <SettingsAccordion
                  :title="$t('INBOX_MGMT.CHANNEL_PREFERENCES')"
                  class="mt-6"
                >
                  <SettingsToggleSection
                    v-model="greetingEnabled"
                    :header="
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.LABEL'
                      )
                    "
                    :description="
                      $t(
                        'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_TOGGLE.HELP_TEXT'
                      )
                    "
                  >
                    <template v-if="greetingEnabled" #editor>
                      <GreetingsEditor
                        v-model="greetingMessage"
                        :label="
                          $t(
                            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.LABEL'
                          )
                        "
                        :placeholder="
                          $t(
                            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_GREETING_MESSAGE.PLACEHOLDER'
                          )
                        "
                        :richtext="!textAreaChannels"
                      />
                    </template>
                  </SettingsToggleSection>

                  <SettingsToggleSection
                    v-if="isAWebWidgetInbox"
                    v-model="emailCollectEnabled"
                    :header="
                      $t('INBOX_MGMT.SETTINGS_POPUP.ENABLE_EMAIL_COLLECT_BOX')
                    "
                    :description="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.ENABLE_EMAIL_COLLECT_BOX_SUB_TEXT'
                      )
                    "
                  />

                  <SettingsToggleSection
                    v-if="isAWebWidgetInbox"
                    v-model="allowMessagesAfterResolved"
                    :header="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.ALLOW_MESSAGES_AFTER_RESOLVED'
                      )
                    "
                    :description="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.ALLOW_MESSAGES_AFTER_RESOLVED_SUB_TEXT'
                      )
                    "
                  />

                  <SettingsToggleSection
                    v-if="isAWebWidgetInbox && showContinuityToggle"
                    v-model="continuityViaEmail"
                    :header="
                      $t(
                        'INBOX_MGMT.SETTINGS_POPUP.ENABLE_CONTINUITY_VIA_EMAIL'
                      )
                    "
                    :description="continuityDescription"
                    :hide-toggle="isContinuityDisabled"
                    :class="
                      isContinuityDisabled
                        ? 'cursor-not-allowed opacity-50'
                        : ''
                    "
                  />
                </SettingsAccordion>

                <div class="w-full flex justify-end items-center py-4 mt-2">
                  <NextButton
                    v-if="isAPIInbox"
                    type="submit"
                    :disabled="v$.webhookUrl.$invalid"
                    :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
                    :is-loading="uiFlags.isUpdating"
                    @click="updateInbox"
                  />
                  <NextButton
                    v-else
                    type="submit"
                    :disabled="v$.$invalid"
                    :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
                    :is-loading="uiFlags.isUpdating"
                    @click="updateInbox"
                  />
                </div>
              </div>

              <div
                v-if="isAWebWidgetInbox"
                class="flex-1 sticky top-4 self-start max-w-lg flex-shrink-0 w-full min-w-0"
              >
                <div
                  class="flex flex-col outline -outline-offset-1 outline-1 outline-n-weak w-full px-3 pt-3 pb-8 bg-n-surface-1 rounded-2xl min-h-[45rem] overflow-hidden"
                >
                  <Widget
                    :welcome-heading="channelWelcomeTitle"
                    :welcome-tagline="channelWelcomeTagline"
                    :website-name="selectedInboxName"
                    :logo="avatarUrl"
                    is-online
                    :reply-time="replyTime"
                    :color="inbox.widget_color"
                    :widget-bubble-position="widgetBubblePosition"
                    :widget-bubble-launcher-title="widgetBubbleLauncherTitle"
                    :widget-bubble-type="widgetBubbleType"
                    :widget-layout="widgetLayout"
                    :web-widget-script="inbox.web_widget_script"
                  />
                </div>
              </div>
            </div>

            <div
              v-if="selectedTabKey === 'collaborators'"
              class="mx-6 max-w-4xl"
            >
              <CollaboratorsPage :inbox="inbox" />
            </div>
            <div
              v-if="selectedTabKey === 'configuration'"
              class="mx-6"
              :class="isAWebWidgetInbox ? 'max-w-7xl' : 'max-w-4xl'"
            >
              <ConfigurationPage :inbox="inbox" />
            </div>
            <div
              v-if="selectedTabKey === 'voice-configuration'"
              class="mx-6 max-w-4xl"
            >
              <VoiceConfigurationPage :inbox="inbox" />
            </div>
            <div
              v-if="selectedTabKey === 'calls-configuration'"
              class="mx-6 max-w-4xl"
            >
              <WhatsappCallingPage :inbox="inbox" />
            </div>
            <div v-if="selectedTabKey === 'csat'">
              <CustomerSatisfactionPage :inbox="inbox" />
            </div>
            <div v-if="selectedTabKey === 'pre-chat-form'">
              <PreChatFormSettings :inbox="inbox" />
            </div>
            <div v-if="selectedTabKey === 'business-hours'">
              <WeeklyAvailability :inbox="inbox" />
            </div>
            <div v-if="selectedTabKey === 'bot-configuration'">
              <BotConfiguration :inbox="inbox" />
            </div>
            <div v-if="selectedTabKey === 'qr-code'" class="mx-6 max-w-4xl">
              <EvolutionApi :inbox="inbox" />
            </div>
            <div v-if="selectedTabKey === 'whatsapp-health'">
              <AccountHealth
                :health-data="healthData"
                :is-registering-webhook="isRegisteringWebhook"
                @register-webhook="registerWebhook"
              />
            </div>
            <div v-if="selectedTabKey === 'whatsapp-templates'">
              <WhatsappTemplatesPage :inbox="inbox" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
